
import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { LucideIcon, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useEditorConfig } from '../../contexts/EditorContext';
import { NodeTypeConfig } from '../../types';

const Sidebar = () => {
  const { nodeTypes, nodeCategories } = useEditorConfig();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      // Auto-collapse on screens smaller than md (768px)
      setIsCollapsed(window.innerWidth < 768);
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    // Store type globally for drop-target highlighting logic in useNodeOperations
    // @ts-ignore
    window.__draggingNodeType = nodeType;
  };

  const onDragEnd = () => {
    // @ts-ignore
    window.__draggingNodeType = null;
  };

  if (isCollapsed) {
    return (
      <aside className="w-12 h-full bg-slate-50 border-r border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col items-center py-4 shadow-[1px_0_0_0_rgba(0,0,0,0.02)] z-10 transition-all duration-300">
        <button 
          onClick={() => setIsCollapsed(false)}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-lg transition-colors mb-4"
          title="Expand Sidebar"
        >
          <PanelLeftOpen size={20} />
        </button>
        <div className="flex-1 flex items-center justify-center">
           <div className="-rotate-90 text-[10px] font-bold text-slate-400 whitespace-nowrap tracking-widest uppercase">
              Node Palette
           </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-64 h-full bg-slate-50 border-r border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col shadow-[1px_0_0_0_rgba(0,0,0,0.02)] z-10 transition-all duration-300">
      <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Node Palette</h2>
            <button 
              onClick={() => setIsCollapsed(true)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              title="Collapse Sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
        </div>
        <div className="relative">
             <Icons.Search className="absolute left-2.5 top-2.5 text-slate-400" size={14}/>
             <input 
                type="text" 
                placeholder="Search nodes..." 
                className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 transition-all placeholder-slate-400"
             />
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {nodeCategories.map(category => (
          <div key={category}>
            <h3 className="text-[10px] font-bold text-slate-400 mb-3 uppercase tracking-wider">{category}</h3>
            <div className="grid grid-cols-2 gap-2">
              {(Object.values(nodeTypes) as NodeTypeConfig[])
                .filter(node => node.category === category)
                .map(node => {
                    const iconMap = Icons as unknown as Record<string, LucideIcon>;
                    const Icon = iconMap[node.icon] || Icons.Box;
                    
                    if (node.disabled) {
                       return (
                        <div
                        key={node.type}
                        className="flex flex-col items-center justify-center p-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg opacity-50 cursor-not-allowed group relative"
                        title="Not available in this version"
                        >
                        <div 
                            className="p-1.5 rounded mb-2 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800"
                            style={{ color: '#94a3b8' }}
                        >
                            <Icon size={16} />
                        </div>
                        <span className="text-[10px] text-center font-medium text-slate-400 leading-tight">
                            {node.label}
                        </span>
                        </div>
                       );
                    }

                    return (
                        <div
                        key={node.type}
                        className="flex flex-col items-center justify-center p-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg cursor-grab hover:shadow-sm hover:border-slate-300 dark:hover:border-slate-600 transition-all group"
                        onDragStart={(event) => onDragStart(event, node.type)}
                        onDragEnd={onDragEnd}
                        draggable
                        >
                        <div 
                            className="p-1.5 rounded mb-2 transition-transform group-hover:scale-105 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800"
                            style={{ color: node.color }}
                        >
                            <Icon size={16} />
                        </div>
                        <span className="text-[10px] text-center font-medium text-slate-600 dark:text-slate-300 leading-tight group-hover:text-slate-900 dark:group-hover:text-white">
                            {node.label}
                        </span>
                        </div>
                    );
                })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default Sidebar;